import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { UserRole, RoleType } from '@magnetar/admin-api-interfaces'
import { userRoleService } from '@/services/user_role_service'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'

const formSchema = z.object({
  role_name: z.string().min(1, '請輸入角色名稱'),
  role_description: z.string().nullable(),
})

type FormValues = z.infer<typeof formSchema>

interface ClinicGroupUserRoleSettingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  role?: UserRole // If provided, we're in edit mode
  onSuccess: () => void // Callback to refresh the data
}

export function ClinicGroupUserRoleSettingDialog({
  open,
  onOpenChange,
  role,
  onSuccess,
}: ClinicGroupUserRoleSettingDialogProps) {
  const { token } = useAuth()
  const { id: clinicGroupId } = useViewMode()
  const isEditMode = !!role

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      role_name: role?.role_name || '',
      role_description: role?.role_description || '',
    },
  })

  const onSubmit = async (values: FormValues) => {
    try {
      // ClinicGroup 層級角色使用 custom role_type
      const payload = {
        role_name: values.role_name,
        role_description: values.role_description || null,
        role_type: 'custom' as RoleType,
        scope_type: 'clinic_group' as const,
        clinic_group_id: clinicGroupId || null,
      }

      let result
      if (isEditMode) {
        result = await userRoleService.updateUserRole(role, payload, token)
      } else {
        result = await userRoleService.createUserRole(payload, token)
      }

      if (result.success) {
        toast.success(`${isEditMode ? '更新' : '新增'}角色成功`)
        form.reset()
        onSuccess()
        onOpenChange(false)
      } else {
        toast.error(`${isEditMode ? '更新' : '新增'}角色失敗`)
      }
    } catch {
      toast.error(`${isEditMode ? '更新' : '新增'}角色失敗`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? '編輯集團角色' : '新增集團角色'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? '修改診所集團角色的基本資料' : '建立一個新的診所集團層級角色'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="role_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入角色名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色描述</FormLabel>
                  <FormControl>
                    <Textarea placeholder="輸入角色描述" className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit">{isEditMode ? '更新' : '建立'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
